"use client"

import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type Ref,
} from "react"
import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar"
import { cva, VariantProps } from "class-variance-authority"
import { cn } from "cn"

import { selectOrganization } from "@/lib/storage/app-slice"
import { useAppSelector } from "@/lib/storage/hooks"
import LogoIcon from "../logo-icon"
import { LottieAnimation } from "./lottie-animation"

export const avatarVariants = cva(
  "relative flex shrink-0 cursor-[inherit] rounded-full font-medium",
  {
    variants: {
      size: {
        "3xl": "size-(--avatar-size-3xl) text-4xl",
        "2xl": "size-(--avatar-size-2xl) text-3xl",
        xl: "size-(--avatar-size-xl) text-2xl",
        lg: "size-(--avatar-size-lg) text-lg",
        md: "size-(--avatar-size-md) text-base",
        sm: "size-(--avatar-size-sm) text-xs",
        xs: "size-(--avatar-size-xs) text-2xs",
        "2xs":
          "size-(--avatar-size-2xs) text-[.5625rem] font-semibold tracking-tight",
        "3xs": "size-(--avatar-size-3xs) text-3xs tracking-tight",
        // Chat thread avatars: 2xs below the @xl/msg container step and sm at
        // or above it, so the size follows the column's width in CSS instead
        // of a measured value in the store. Only valid inside @container/msg.
        msg: cn(
          "size-(--msg-avatar-size) text-[.5625rem] font-semibold tracking-tight",
          "@xl/msg:text-xs @xl/msg:font-medium @xl/msg:tracking-normal"
        ),
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

export type AvatarSizes = VariantProps<typeof avatarVariants>["size"]

export interface AvatarProps
  extends
    ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

function Avatar({
  ref,
  className,
  size,
  ...props
}: AvatarProps & {
  ref?: Ref<ComponentRef<typeof AvatarPrimitive.Root>>
}) {
  return (
    <AvatarPrimitive.Root
      ref={ref}
      data-slot="avatar"
      className={cn(avatarVariants({ size }), className)}
      {...props}
    />
  )
}
Avatar.displayName = AvatarPrimitive.Root.displayName

function AvatarImage({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & {
  ref?: Ref<ComponentRef<typeof AvatarPrimitive.Image>>
}) {
  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn(
        "aspect-square size-full overflow-hidden rounded-full object-cover",
        className
      )}
      {...props}
    />
  )
}
AvatarImage.displayName = AvatarPrimitive.Image.displayName

function AvatarFallback({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
  ref?: Ref<ComponentRef<typeof AvatarPrimitive.Fallback>>
}) {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex size-full items-center justify-center rounded-full bg-gray-4 text-gray-12 uppercase select-none",
        className
      )}
      {...props}
    />
  )
}
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

interface BotAvatarProps extends AvatarProps {
  animated?: boolean
}

const BotAvatar = ({
  "aria-label": ariaLabel,
  animated = false,
  ...props
}: BotAvatarProps) => {
  const organization = useAppSelector(selectOrganization)
  const accentColor = organization?.properties.accent_color
  return (
    <Avatar {...props}>
      <AvatarFallback
        className={cn(
          animated
            ? accentColor
              ? "bg-accent-foreground text-accent"
              : "bg-transparent text-primary"
            : accentColor
              ? "bg-accent text-accent-foreground"
              : "bg-primary text-primary-foreground",
          props.className
        )}
      >
        {animated ? (
          <LottieAnimation
            name="logo-loading"
            width="100%"
            height="100%"
            eager
          />
        ) : (
          <LogoIcon
            size="relative"
            className="size-3/5"
            aria-label={ariaLabel}
            aria-hidden={!ariaLabel ? true : undefined}
          />
        )}
      </AvatarFallback>
    </Avatar>
  )
}

export { Avatar, AvatarFallback, AvatarImage, BotAvatar }
